import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IconX, IconChevronRight, IconChevronLeft, IconCompass } from "@tabler/icons-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * TourCoach — role-based guided tour that runs once per role per browser.
 * - Detects role from AuthContext, picks steps, highlights DOM elements via data-testid.
 * - Persistence in localStorage ("chiessa.tour.done.{role}").
 * - Users can re-launch from AppShell "Ver tour" link.
 */

// ---------- Tour scripts per role ----------
function useTourScript(role, lang) {
  return useMemo(() => {
    const scripts = {
      familia: [
        {
          selector: "[data-testid='app-module-circulo']",
          route: "/app",
          title_es: "Círculo — el expediente de tu hijo",
          title_en: "Círculo — your child's file",
          body_es: "Aquí llevas la bitácora, comparte con docentes y sombra, y recibes reportes semanales. Es el centro de la red que rodea a tu hijo.",
          body_en: "Log daily observations, share with teachers and shadow, and receive weekly reports. This is the center of your child's network.",
        },
        {
          selector: "[data-testid='app-billing-link']",
          route: "/app",
          title_es: "Facturación — plan Familia",
          title_en: "Billing — Family plan",
          body_es: "$20 al mes por hijo (antes $27). Cancelas cuando quieras. También compras cursos sueltos o consultas.",
          body_en: "$20/month per child (was $27). Cancel anytime. Also buy individual courses or consultations.",
        },
      ],
      docente: [
        {
          selector: "[data-testid='app-module-aula']",
          route: "/app",
          title_es: "Aula — tu formación continua",
          title_en: "Aula — your professional development",
          body_es: "Cursos cortos de neurodivergencia. Marca lecciones completadas y tu progreso se conectará con la evidencia de mejora en tus estudiantes.",
          body_en: "Short neurodivergence courses. Mark lessons complete; your progress links to student improvement evidence.",
        },
        {
          selector: "[data-testid='app-billing-link']",
          route: "/app",
          title_es: "Facturación — cursos y suscripciones",
          title_en: "Billing — courses and subscriptions",
          body_es: "Compra cursos sueltos ($27) o accede vía el plan de tu escuela.",
          body_en: "Buy individual courses ($27) or access via your school plan.",
        },
      ],
      sombra: [
        {
          selector: "[data-testid='app-module-sello']",
          route: "/app",
          title_es: "Sello Sombra — tu certificación",
          title_en: "Sello Sombra — your certification",
          body_es: "Completa 6 pasos + entrevista con Mariela para obtener el sello Chiessa. Verificable por QR y con badge para tu bio.",
          body_en: "Complete 6 steps + interview to earn the Chiessa seal. QR-verifiable + embeddable badge for your bio.",
        },
        {
          selector: "[data-testid='app-billing-link']",
          route: "/app",
          title_es: "Facturación — certificación Sello",
          title_en: "Billing — Sello certification",
          body_es: "La certificación tiene un costo único de $197 (antes $297) e incluye tu perfil público con QR + badge embed.",
          body_en: "One-time $197 certification (was $297) includes your public QR profile + embed badge.",
        },
      ],
      escuela: [
        {
          selector: "[data-testid='app-module-brujula']",
          route: "/app",
          title_es: "Brújula — panel de tu escuela",
          title_en: "Brújula — your school panel",
          body_es: "Índice de inclusión, simulacro de crisis, generador de adecuaciones curriculares y carpeta de defensa institucional.",
          body_en: "Inclusion index, crisis simulator, curriculum adjustments generator and defense folder.",
        },
        {
          selector: "[data-testid='app-billing-link']",
          route: "/app",
          title_es: "Plan Escuela",
          title_en: "School Plan",
          body_es: "$427 al año por escuela con acceso completo a Brújula y gestión inclusiva institucional.",
          body_en: "$427/year per school with full Brújula access and inclusive management.",
        },
      ],
      psicologa: [
        {
          selector: "[data-testid='app-tool-tablero']",
          route: "/app",
          title_es: "Tablero — control de la red",
          title_en: "Dashboard — network control",
          body_es: "KPIs de toda la operación, composición de la red y simulador de ingresos para proyectar tu negocio.",
          body_en: "Full operation KPIs, network composition and revenue simulator.",
        },
        {
          selector: "[data-testid='app-tool-boletin']",
          route: "/app",
          title_es: "Boletín — comunicación semanal",
          title_en: "Bulletin — weekly comms",
          body_es: "Sistema Inteligente redacta el boletín semanal desde las señales reales de la red. Editas y copias a tu ESP.",
          body_en: "Sistema Inteligente drafts the weekly bulletin from real network signals. Edit and copy to your ESP.",
        },
        {
          selector: "[data-testid='app-tool-evidencia']",
          route: "/app",
          title_es: "Evidencia — impacto formativo",
          title_en: "Evidence — training impact",
          body_es: "Liga cada curso aprobado con mejoras en los estudiantes del docente. Vender inclusión con datos.",
          body_en: "Link each completed course with improvement in the teacher's students. Sell inclusion with data.",
        },
        {
          selector: "[data-testid='app-tool-historial']",
          route: "/app",
          title_es: "Historial — registros conversacionales",
          title_en: "History — conversational records",
          body_es: "Ver y descargar CSV UTF-8 de bitácoras Círculo, notas Sintonía, aplicaciones Sello, evaluaciones Brújula y progreso Aula. Filtros por fecha.",
          body_en: "View and download CSV UTF-8 of Círculo logs, Sintonía notes, Sello apps, Brújula assessments and Aula progress. Date filters.",
        },
      ],
    };
    return scripts[role] || [];
  }, [role, lang]);
}


export default function TourCoach() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const location = useLocation();
  const nav = useNavigate();
  const role = user?.role;
  const steps = useTourScript(role, lang);
  const storageKey = role ? `chiessa.tour.done.${role}` : null;
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Auto-start when a user just logged in and no completion flag exists.
  useEffect(() => {
    if (!user || !storageKey) return;
    const done = localStorage.getItem(storageKey);
    // Only auto-start on /app (home) to make sure targets are present
    if (!done && location.pathname === "/app") {
      const t = setTimeout(() => setActive(true), 900);
      return () => clearTimeout(t);
    }
  }, [user, storageKey, location.pathname]);

  // Listen for a global event so buttons elsewhere can re-launch
  useEffect(() => {
    const h = () => { setIdx(0); setActive(true); };
    window.addEventListener("chiessa:tour:start", h);
    return () => window.removeEventListener("chiessa:tour:start", h);
  }, []);

  // Measure current step target — coords are viewport-relative because parent is position:fixed
  const measure = useCallback(() => {
    if (!active || !steps[idx]) { setRect(null); return; }
    const el = document.querySelector(steps[idx].selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [active, idx, steps]);

  // Separate effect for scrollIntoView so it only runs once per step change (not per measurement)
  useEffect(() => {
    if (!active || !steps[idx]) return;
    const el = document.querySelector(steps[idx].selector);
    if (el) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
    }
  }, [active, idx, steps]);

  useEffect(() => {
    if (!active) return;
    const step = steps[idx];
    if (step?.route && location.pathname !== step.route) {
      nav(step.route);
    }
    const t = setTimeout(measure, 350);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, idx, steps, location.pathname, nav, measure]);

  const finish = useCallback(() => {
    if (storageKey) localStorage.setItem(storageKey, "1");
    setActive(false);
    setIdx(0);
  }, [storageKey]);

  if (!active || !steps.length) return null;
  const step = steps[idx];
  if (!step) return null;
  const isFirst = idx === 0;
  const isLast = idx === steps.length - 1;

  // Popover positioning — viewport-relative (parent is position:fixed), clamped, responsive width
  const popoverPos = (() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(360, vw - 24);
    if (!rect) {
      return {
        top: Math.max(16, (vh - 240) / 2),
        left: Math.max(12, (vw - width) / 2),
        width,
      };
    }
    const popH = 240;
    const belowSpace = vh - (rect.top + rect.height);
    const aboveSpace = rect.top;
    // Prefer below; if not enough, try above; else center
    let top;
    if (belowSpace >= popH + 16) {
      top = rect.top + rect.height + 16;
    } else if (aboveSpace >= popH + 16) {
      top = rect.top - popH - 16;
    } else {
      top = Math.max(16, (vh - popH) / 2);
    }
    top = Math.max(12, Math.min(top, vh - popH - 12));
    const left = Math.max(12, Math.min(rect.left + rect.width / 2 - width / 2, vw - width - 12));
    return { top, left, width };
  })();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: "auto" }} data-testid="tour-coach">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0"
          style={{ background: "rgba(10, 22, 20, 0.55)", backdropFilter: "blur(1.5px)" }}
          onClick={() => { /* prevent closing on backdrop click */ }}
        />

        {/* Spotlight (donut) */}
        {rect && (
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="absolute rounded-xl"
            style={{
              top: rect.top - 6,
              left: rect.left - 6,
              width: rect.width + 12,
              height: rect.height + 12,
              boxShadow: "0 0 0 4px rgba(201,145,72,0.9), 0 0 0 9999px rgba(10,22,20,0.55)",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Popover */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28 }}
          className="absolute rounded-xl bg-paper border border-line shadow-2xl p-5"
          style={popoverPos}
          data-testid="tour-popover"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-honey text-white flex items-center justify-center">
                <IconCompass size={14} />
              </div>
              <p className="lbl m-0">Tour · {idx + 1}/{steps.length}</p>
            </div>
            <button
              onClick={finish}
              aria-label={lang === "es" ? "Cerrar tour" : "Close tour"}
              className="text-tert hover:text-ink p-2 -m-2"
              data-testid="tour-close"
            >
              <IconX size={16} />
            </button>
          </div>
          <h3 className="font-display text-[19px] font-medium mt-3 leading-tight" data-testid="tour-title">
            {lang === "es" ? step.title_es : step.title_en}
          </h3>
          <p className="text-muted text-[13.5px] mt-2 leading-relaxed" data-testid="tour-body">
            {lang === "es" ? step.body_es : step.body_en}
          </p>
          <div className="flex items-center justify-between mt-5">
            <button
              disabled={isFirst}
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              className="btn-ghost btn text-[12px] disabled:opacity-30"
              data-testid="tour-prev"
            >
              <IconChevronLeft size={12} /> {lang === "es" ? "Atrás" : "Back"}
            </button>
            {isLast ? (
              <button onClick={finish} className="btn text-[12px]" data-testid="tour-finish">
                {lang === "es" ? "Listo" : "Done"}
              </button>
            ) : (
              <button
                onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}
                className="btn text-[12px]"
                data-testid="tour-next"
              >
                {lang === "es" ? "Siguiente" : "Next"} <IconChevronRight size={12} />
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
